import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IAppState } from '../../../lib/types/AppState';
import { ISelectedJob } from '../../../lib/types/AppState/LandingPage.interface';
import { useHistory } from 'react-router-dom';
import { Stack } from '@mantine/core';
import TabsContainer from './Tabs/TabsContainer';
import { IOrgJob } from './PropagateMatchesTypes';
import { getOrgJobsData } from './PropagateMatchesAPIs';
import { addSelectedJobs, clearAllSelections } from '../LandingPage/LandingPageActions';

import './PropagateMatches.scss';

export default function PropagateMatches() {
  const dispatch = useDispatch();
  const history = useHistory();
  const jobIDFromHistory: number | null = history.location?.state?.jobID;
  const globalStateSelectedJobs = useSelector((state: IAppState) => state.landingPage.landingPageReducer.selectedJobs);

  const [orgJobs, setOrgJobs] = useState<IOrgJob[]>([]);
  const [selectedJobsFromStorage, setSelectedJobsFromStorage] = useState<ISelectedJob[] | null>(() => {
    const storedSelectedJobs = window.localStorage.getItem('selectedJobs');
    return storedSelectedJobs ? JSON.parse(storedSelectedJobs) : null;
  });

  const [selectedJobs, setSelectedJobs] = useState<ISelectedJob[]>(() => {
    const selectedJobFromHistory = jobIDFromHistory ? [{ jobID: jobIDFromHistory }] : [];
    return globalStateSelectedJobs.length ? globalStateSelectedJobs : selectedJobFromHistory;
  });

  const navigateToMarketPricing = useCallback(() => {
    history.push('/market-analysis/market-pricing', {
      notFirstLoad: true,
      searchQuery: history.location?.state?.searchQuery,
      offset: 0,
      bulkPricing: false,
    });
  }, [history]);

  const fetchOrgJobsData = useCallback(async (jobData: Promise<IOrgJob[]>) => {
    const orgJobsData = await jobData;
    if (selectedJobs.length && orgJobsData.length !== selectedJobs.length) {
      navigateToMarketPricing();
    } else {
      setOrgJobs(orgJobsData);
    }
  }, [selectedJobs, navigateToMarketPricing]);

  const fetchDataAndUpdateStorage = useCallback(() => {
    const promisedData = getOrgJobsData(selectedJobs);
    fetchOrgJobsData(promisedData);
    window.localStorage.setItem('selectedJobs', JSON.stringify(selectedJobs));
    setSelectedJobsFromStorage(selectedJobs);
  }, [fetchOrgJobsData, selectedJobs]);



  const resetSelectedJobsFromStorage = useCallback(() => {
    dispatch(clearAllSelections());
    dispatch(addSelectedJobs(selectedJobsFromStorage));
    setSelectedJobs(selectedJobsFromStorage as ISelectedJob[]);
  }, [dispatch, selectedJobsFromStorage]);

  useEffect(() => {
    if (!selectedJobs.length && selectedJobsFromStorage) {
      resetSelectedJobsFromStorage();
    } else if (!selectedJobs.length && !selectedJobsFromStorage) {
      navigateToMarketPricing();
    } else if (selectedJobs.length) {
      fetchDataAndUpdateStorage();
    }
  }, [
    fetchDataAndUpdateStorage,
    navigateToMarketPricing,
    resetSelectedJobsFromStorage,
    selectedJobs,
    selectedJobsFromStorage,
  ]);

  return (
    <Stack className="propagate-matches">
      {orgJobs?.length > 0 && <TabsContainer orgJobs={orgJobs} />}
    </Stack>
  );
}
